class BundleColorSelector {
  constructor(container) {
    this.container = container || document.querySelector('[data-bundle-color-selector]');
    if (!this.container) return;

    this.selectors = {
      trigger: '[data-bundle-color-trigger]',
      drawer: '[data-bundle-color-drawer]',
      closeButton: '[data-bundle-color-close]',
      option: '[data-bundle-color-option]',
      variantSelect: 'select[name="id"], [data-product-select]',
      quantity: '[data-bundle-quantity]',
      price: '[data-bundle-price]',
      variantTitle: '[data-bundle-variant-title]',
      dynamicVariantsContainer: '[data-dynamic-variants-enabled]'
    };

    this.drawer = this.container.querySelector(this.selectors.drawer);
    this.trigger = this.container.querySelector(this.selectors.trigger);
    this.closeButton = this.container.querySelector(this.selectors.closeButton);

    // Main product variant select & size buttons (shared for the whole product)
    this.variantSelect = document.querySelector(this.selectors.variantSelect) || null;
    this.dynamicVariantsContainer = document.querySelector(this.selectors.dynamicVariantsContainer) || null;

    // Bundle product ID for fetching variant data if needed
    this.bundleProductId = this.container.getAttribute('data-bundle-product-id');

    const indexAttr = this.container.getAttribute('data-bundle-index');
    this.isPrimary = (indexAttr == null || indexAttr == '0');

    this.classes = {
      active: 'is-active',
      selected: 'is-selected'
    };

    this.init();
  }

  init() {
    if (!this.container || !this.drawer || !this.trigger) return;

    this.bindEvents();
    this.setInitialSelected();

    this.container.setAttribute('data-initialized', 'true');
  }

  bindEvents() {
      // Open/Close drawer
      this.trigger.addEventListener('click', () => this.openDrawer());
      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => this.closeDrawer());
      }

      // Option click
      const options = this.container.querySelectorAll(this.selectors.option);
      options.forEach(option => {
        option.addEventListener('click', () => this.handleOptionClick(option));
      });

      // Listen for variant:change event – all selectors should listen
      document.addEventListener('variant:change', (event) => {
        this.handleVariantChange(event);
      });
    }

    setInitialSelected() {
      const options = this.drawer.querySelectorAll('[data-bundle-color-option]');
      if (!options.length) return;

      // data-initial-selected is set in Liquid for the preselected variant
      let initialOption = this.drawer.querySelector('[data-bundle-color-option][data-initial-selected]');
      if (!initialOption) {
        initialOption = options[0];
      }

      options.forEach(option => {
        option.classList.toggle(this.classes.selected, option === initialOption);
      });

      const variantTitle = initialOption.dataset.variantTitle;

      // Get quantity and price directly from the option's data attributes
      // These are set correctly in bundle-color-selector.liquid
      const quantity = parseInt(initialOption.dataset.quantity) || 1;
      const price = parseFloat(initialOption.dataset.price) || 0;

      this.updateTrigger(variantTitle, quantity, price);
    }

  getCurrentSize() {
    // Preferred: from the size radio buttons
    if (this.dynamicVariantsContainer) {
      const checked = this.dynamicVariantsContainer.querySelector(
        '.variant-input[data-index="option1"] input[type="radio"]:checked'
      );
      if (checked) return checked.value;
    }

    // Fallback: from the <select name="id">
    if (this.variantSelect) {
      const selectedId = this.variantSelect.value;
      // Try to get size from the selected option in the bundle drawer
      const selectedOption = this.container.querySelector(`${this.selectors.option}.${this.classes.selected}`);
      if (selectedOption && selectedOption.dataset.variantId === selectedId) {
        // Size information would be in the variant title or option data
        // This is a fallback - main logic should use dynamic variants container
      }
    }

    return null;
  }

  openDrawer() {
    document.body.classList.add('bundle-color-drawer-open');
    this.drawer.classList.add(this.classes.active);
    if (window.theme && theme.a11y) theme.a11y.trapFocus({ container: this.drawer });
  }

  closeDrawer() {
    document.body.classList.remove('bundle-color-drawer-open');
    this.drawer.classList.remove(this.classes.active);
    if (window.theme && theme.a11y) theme.a11y.removeTrapFocus({ container: this.drawer });
  }

    handleOptionClick(option) {
      const variantId = option.dataset.variantId;
      const variantTitle = option.dataset.variantTitle;
      
      // Use default quantity for this selector position (fixed quantity per position)
      const defaultQuantity = parseInt(this.container.dataset.defaultQuantity) || 1;
      
      // Calculate price based on default quantity
      const price = this.calculatePrice(variantId, defaultQuantity);

      // Local selection state
      this.container.querySelectorAll(this.selectors.option).forEach(opt => {
        opt.classList.remove(this.classes.selected);
      });
      option.classList.add(this.classes.selected);

      // Update option data with default quantity
      option.dataset.quantity = defaultQuantity;
      option.dataset.price = price;

      this.updateTrigger(variantTitle, defaultQuantity, price);
      this.closeDrawer();
    }

  updateDynamicVariant(variantTitle) {
    if (!this.dynamicVariantsContainer || !variantTitle) return;

    // Find variant input/button with matching data-value
    // Try exact match first
    let variantInput = this.dynamicVariantsContainer.querySelector(
      `.variant-input[data-value="${variantTitle}"]`
    );

    // If exact match not found, try to find by partial match
    if (!variantInput) {
      const allVariantInputs = this.dynamicVariantsContainer.querySelectorAll('.variant-input');
      allVariantInputs.forEach(inputEl => {
        const dataValue = inputEl.dataset.value;
        if (dataValue && (
          dataValue === variantTitle ||
          dataValue.includes(variantTitle) ||
          variantTitle.includes(dataValue)
        )) {
          variantInput = inputEl;
        }
      });
    }

    if (variantInput) {
      // Find the input or label inside to trigger click
      const input = variantInput.querySelector('input[type="radio"]');
      const label = variantInput.querySelector('label');

      if (input && !input.disabled && !input.classList.contains('disabled')) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('click', { bubbles: true }));
      } else if (label && !label.classList.contains('disabled')) {
        label.click();
      }
    }
  }

    handleVariantChange(event) {
      // Get variant from event detail (theme.js dispatches variant:change with variant in detail)
      const variant = event.detail?.variant;
      const variantId = variant?.id || event.detail?.variantId;
      
      if (!variantId) {
        console.warn('Bundle selector: No variant ID found in event', event.detail);
        return;
      }

      try {
        // Get metafield data from script tag or fetch from API
        let preselectedData = null;
        const metafieldJsonEl = document.querySelector('[data-variant-metafields-json]');
        
        if (metafieldJsonEl) {
          // Use script tag if available
          const metafieldData = JSON.parse(metafieldJsonEl.textContent);
          const variantData = metafieldData.variants.find(v => v.id.toString() === variantId.toString());
          if (variantData && variantData.preselected_bundle_color_with_quantity) {
            preselectedData = variantData.preselected_bundle_color_with_quantity;
          }
        } else {
          // Fallback: Log warning - script tag should be added to product-template.liquid
          console.warn('Bundle selector: data-variant-metafields-json script tag not found. Variant change updates may not work correctly.');
          preselectedData = null;
        }

        // Get all bundle selectors on the page (sorted by index)
        const allSelectors = Array.from(document.querySelectorAll('[data-bundle-color-selector]'))
          .sort((a, b) => {
            const indexA = parseInt(a.dataset.bundleIndex) || 0;
            const indexB = parseInt(b.dataset.bundleIndex) || 0;
            return indexA - indexB;
          });
        
        if (preselectedData && preselectedData.length > 0) {
          // Parse all entries
          const entries = [];
          preselectedData.forEach(entry => {
            if (entry && entry.includes('*')) {
              const parts = entry.split('*');
              entries.push({
                variantName: parts[0].trim(),
                quantity: parseInt(parts[1].trim()) || 1
              });
            }
          });

          // Update each selector based on its index
          allSelectors.forEach((selector, index) => {
            const selectorInstance = selector.bundleColorSelectorInstance;
            
            if (index < entries.length) {
              // Show selector and update it
              const parentBlock = selector.closest('.product-block');
              if (parentBlock && parentBlock.querySelector('[data-bundle-color-selector]') === selector) {
                // Only show if this product-block directly contains this bundle selector
                parentBlock.style.display = '';
                parentBlock.style.visibility = '';
              }
              selector.style.display = '';
              selector.style.visibility = '';
              
              const entry = entries[index];
              // Update default quantity for this position
              selector.dataset.defaultQuantity = entry.quantity;
              
              if (selectorInstance) {
                selectorInstance.updateBundleSelectorForVariant(entry.variantName, entry.quantity);
              }
            } else {
              // Hide selector if no entry for this position
              // Hide the product-block that directly contains this bundle selector
              const parentBlock = selector.closest('.product-block');
              if (parentBlock && parentBlock.querySelector('[data-bundle-color-selector]') === selector) {
                // Only hide if this product-block directly contains this bundle selector
                parentBlock.style.display = 'none';
              }
              selector.style.display = 'none';
              selector.querySelectorAll('[data-bundle-color-option].is-selected').forEach(btn => btn.classList.remove('is-selected'));
            }
          });
        } else {
          // Hide all selectors if no preselected data
          allSelectors.forEach(selector => {
            const parentBlock = selector.closest('.product-block');
            if (parentBlock && parentBlock.querySelector('[data-bundle-color-selector]') === selector) {
              // Only hide if this product-block directly contains this bundle selector
              parentBlock.style.display = 'none';
            }
            selector.style.display = 'none';
            selector.querySelectorAll('[data-bundle-color-option].is-selected').forEach(btn => btn.classList.remove('is-selected'));
          });
        }
      } catch (e) {
        console.warn('Failed to update bundle selector from variant change', e);
      }
    }

    updateBundleSelectorForVariant(variantName, quantity) {
      const options = this.container.querySelectorAll(this.selectors.option);
      if (!options.length) return;

      let matchedOption = null;
      
      // Update default quantity for this selector position
      this.container.dataset.defaultQuantity = quantity;
      
      // Find and update the matching option
      options.forEach(option => {
        const variantTitle = option.dataset.variantTitle;
        const matches = variantTitle === variantName || 
                       variantTitle.includes(variantName) || 
                       variantName.includes(variantTitle);

        if (matches) {
          matchedOption = option;
          const variantId = option.dataset.variantId;
          const price = this.calculatePrice(variantId, quantity);
          
          // Update option data with new quantity
          option.dataset.quantity = quantity;
          option.dataset.price = price;
          option.classList.add(this.classes.selected);
          
          // Update container data attributes
          this.container.dataset.preselectedVariantName = variantName;
          this.container.dataset.preselectedQuantity = quantity;
        } else {
          option.classList.remove(this.classes.selected);
          // Update other options' quantities to match default (for when color changes)
          const defaultQty = parseInt(this.container.dataset.defaultQuantity) || quantity;
          const variantId = option.dataset.variantId;
          const price = this.calculatePrice(variantId, defaultQty);
          option.dataset.quantity = defaultQty;
          option.dataset.price = price;
        }
      });

      // Update trigger with matched option
      if (matchedOption) {
        const variantTitle = matchedOption.dataset.variantTitle;
        const qty = matchedOption.dataset.quantity || quantity;
        const price = matchedOption.dataset.price || 0;
        this.updateTrigger(variantTitle, qty, price);
      } else {
        // If no match found, try to find by partial match or use first option
        const firstOption = options[0];
        if (firstOption) {
          const defaultQty = parseInt(this.container.dataset.defaultQuantity) || quantity;
          const variantId = firstOption.dataset.variantId;
          const price = this.calculatePrice(variantId, defaultQty);
          firstOption.classList.add(this.classes.selected);
          firstOption.dataset.quantity = defaultQty;
          firstOption.dataset.price = price;
          this.updateTrigger(firstOption.dataset.variantTitle, defaultQty, price);
        }
      }
    }

    updateFromDataAttributes() {
      const options = this.container.querySelectorAll(this.selectors.option);
      if (!options.length) return;

      let selectedOption = this.container.querySelector(
        `${this.selectors.option}.${this.classes.selected}`
      );
      if (!selectedOption) {
        selectedOption = options[0];
        selectedOption.classList.add(this.classes.selected);
      }

      // Get quantity and price directly from the option's data attributes
      // These are set correctly in bundle-color-selector.liquid
      const variantTitle = selectedOption.dataset.variantTitle;
      const quantity = parseInt(selectedOption.dataset.quantity) || 1;
      const price = parseFloat(selectedOption.dataset.price) || 0;
      
      this.updateTrigger(variantTitle, quantity, price);
    }

    getVariantMetafieldFromProduct(variantId) {
      console.warn('Bundle selector: Metafield JSON script tag not found. Please add data-variant-metafields-json script tag to product-template.liquid');
      return null;
    }

    calculatePrice(variantId, quantity) {
      const option = this.container.querySelector(`[data-bundle-color-option][data-variant-id="${variantId}"]`);
      if (option) {
        const defaultQuantity = parseInt(this.container.dataset.defaultQuantity) || 1;
        const priceForDefaultQty = parseFloat(option.dataset.price) || 0;
         const unitPrice = parseFloat(option.dataset.unitPrice);
        if (!isNaN(unitPrice)) {
          return unitPrice * quantity;
        }
        
        if (quantity === defaultQuantity) {
          return priceForDefaultQty;
        } else if (defaultQuantity > 0) {
          const pricePerUnit = priceForDefaultQty / defaultQuantity;
          return pricePerUnit * quantity;
        }
        return priceForDefaultQty;
      }
      
      // Fallback: return 0 if option not found
      return 0;
    }

  updateTrigger(title, quantity, price) {
    if (!this.trigger) return;
    const titleEl = this.trigger.querySelector(this.selectors.variantTitle);
    const quantityEl = this.trigger.querySelector(this.selectors.quantity);
    const priceEl = this.trigger.querySelector(this.selectors.price);
    const imageContainer = this.trigger.querySelector('.bundle-color-trigger__image-container');

    // Update text content
    if (titleEl) titleEl.textContent = title;
    if (quantityEl) quantityEl.textContent = `${quantity} PCS.`;
    
    // Format price - handle both cents and regular format
    if (priceEl) {
      if (window.theme && theme.Currency) {
        priceEl.textContent = theme.Currency.formatMoney(price);
      } else {
        // Fallback: format as money string
        priceEl.textContent = typeof price === 'number' ? `$${(price / 100).toFixed(2)}` : price;
      }
    }

    // Update trigger image to match selected option from the drawer
    if (imageContainer) {
      try {
        // Find the matching option button by data attribute or selected class
        const options = Array.from(this.container.querySelectorAll(this.selectors.option));
        let matchedOption = options.find(opt => opt.dataset.variantTitle === title);
        if (!matchedOption) matchedOption = this.container.querySelector(`${this.selectors.option}.is-selected`) || options[0];

        if (matchedOption) {
          const sourceImageEl = matchedOption.querySelector('.bundle-color-option__image');
          // Clear existing image content
          imageContainer.innerHTML = '';

          if (sourceImageEl) {
            // If the source has an <img>, clone it and apply trigger classes/attributes
            const img = sourceImageEl.querySelector('img');
            if (img) {
              const newImg = img.cloneNode(true);
              // Ensure trigger image class and sensible dimensions similar to Liquid markup
              newImg.classList.add('bundle-color-trigger__image');
              // Prefer existing width/height if present, else set defaults
              if (!newImg.hasAttribute('width')) newImg.setAttribute('width', '40');
              if (!newImg.hasAttribute('height')) newImg.setAttribute('height', '40');
              imageContainer.appendChild(newImg);
            } else {
              // Otherwise copy inline style (background-color / background-image) into a trigger div
              const newDiv = document.createElement('div');
              newDiv.className = 'bundle-color-trigger__image';
              const style = sourceImageEl.getAttribute('style') || '';
              if (style) newDiv.setAttribute('style', style);
              imageContainer.appendChild(newDiv);
            }
          } else {
            // Fallback placeholder
            const placeholder = document.createElement('div');
            placeholder.className = 'bundle-color-trigger__image-placeholder';
            imageContainer.appendChild(placeholder);
          }
        }
      } catch (e) {
        // Fail silently to avoid breaking bundle selector if DOM differs
        // console.warn('Failed to update bundle trigger image', e);
      }
    }
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  initBundleColorSelectors();
  syncBundleSelectorsToCurrentVariant();
});

document.addEventListener('section:load', (event) => {
  if (event.target.querySelector('[data-bundle-color-selector]')) {
    initBundleColorSelectors();
    syncBundleSelectorsToCurrentVariant();
  }
});

function initBundleColorSelectors() {
  document.querySelectorAll('[data-bundle-color-selector]').forEach(container => {
    if (!container.hasAttribute('data-initialized')) {
      const instance = new BundleColorSelector(container);
      // Store instance reference for external access
      if (instance && instance.container) {
        instance.container.bundleColorSelectorInstance = instance;
      }
      container.setAttribute('data-initialized', 'true');
    }
  });
}
function syncBundleSelectorsToCurrentVariant(){
  try {
    // Try getting the currently selected variant from embedded JSON
    const currentVariantEl = document.querySelector('[data-current-variant-json]');
    let currentVariant = null;
    if (currentVariantEl) {
      currentVariant = JSON.parse(currentVariantEl.textContent);
    }
    // Fallback to master select
    const masterSelect = document.querySelector('select[name="id"],[data-product-select]');
    const variantId = currentVariant?.id || (masterSelect && masterSelect.value);
    if (!variantId) return;

    // Call the same logic used on change so visibility matches on load
    document.querySelectorAll('[data-bundle-color-selector]').forEach(container => {
      const inst = container.bundleColorSelectorInstance;
      if (inst && typeof inst.handleVariantChange === 'function') {
        inst.handleVariantChange({ detail: { variant: { id: variantId } } });
      }
    });
  } catch (e) {
    console.warn('Bundle selector: failed to sync on load', e);
  }
}